import { useState, useEffect, useMemo } from "react";
import { social, getPlatformInfo, type SocialAccount, type SocialPost, type PlatformResult, type PostAnalytics } from "../lib/social";
import { navigate } from "../lib/router";
import { useStaticData } from "../lib/static-data";
import { PlatformIcon } from "../components/PlatformIcon";
import { useAccounts } from "../lib/app-data";
import { PostListSkeleton } from "../components/Skeletons";

// Platforms that support editing/deleting via API
const EDITABLE_PLATFORMS = new Set(["facebook", "youtube", "telegram"]);
const DELETABLE_PLATFORMS = new Set(["twitter", "facebook", "youtube", "pinterest", "telegram"]);

function StatusBadge({ status, scheduledFor }: { status: string; scheduledFor?: string }) {
  const colors: Record<string, string> = {
    published: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
    publishing: "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400",
    partial: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400",
    failed: "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400",
    draft: "bg-stone-100 text-stone-500 dark:bg-zinc-800 dark:text-zinc-500",
    scheduled: "bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400",
  };

  if (status === "scheduled" && scheduledFor) {
    const d = new Date(scheduledFor);
    const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + " " + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${colors.scheduled}`}>
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {label}
      </span>
    );
  }

  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${colors[status] || colors.draft}`}>
      {status}
    </span>
  );
}

function PlatformResultItem({ result }: { result: PlatformResult }) {
  const info = getPlatformInfo(result.platform);
  const isSuccess = result.status === "success" || result.status === "published";
  const isPending = ["draft", "pending", "queued", "scheduled", "skipped"].includes(result.status);
  const isFailed = !isSuccess && !isPending;

  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs ${
        isSuccess
          ? "border-emerald-200/60 dark:border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-500/5"
          : isPending
          ? "border-stone-200/60 dark:border-zinc-800 bg-stone-50 dark:bg-zinc-800/50"
          : "border-red-200/60 dark:border-red-500/20 bg-red-50/50 dark:bg-red-500/5"
      }`}
      title={result.error || ""}
    >
      <span className={`w-5 h-5 ${info.color} text-white rounded flex items-center justify-center flex-shrink-0`}>
        <PlatformIcon platform={result.platform} className="w-3 h-3" />
      </span>
      <div className="flex-1 min-w-0">
        <span className={`font-medium truncate block ${
          isSuccess
            ? "text-emerald-700 dark:text-emerald-300"
            : isPending
            ? "text-stone-500 dark:text-zinc-500"
            : "text-red-700 dark:text-red-300"
        }`}>
          {result.account_name || info.name}
        </span>
        {isFailed && result.error && (
          <span className="text-red-500 dark:text-red-400 text-[10px] truncate block">
            {result.error}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {isSuccess && <span className="text-emerald-500 dark:text-emerald-400">&#10003;</span>}
        {isPending && <span className="text-stone-400 dark:text-zinc-600">{result.status === "pending" ? "ready" : result.status}</span>}
        {isFailed && <span className="text-red-500">&#10007;</span>}
        {result.platform_post_url && (
          <a
            href={result.platform_post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            view
          </a>
        )}
      </div>
    </div>
  );
}

export function Posts() {
  const ssrPosts = useStaticData<SocialPost[]>("posts");
  const { accounts } = useAccounts();
  const accountMap = useMemo<Record<string, SocialAccount>>(() => {
    const map: Record<string, SocialAccount> = {};
    accounts.forEach((a) => (map[String(a.id)] = a));
    return map;
  }, [accounts]);
  const [posts, setPosts] = useState<SocialPost[]>(ssrPosts || []);
  const [loading, setLoading] = useState(!ssrPosts);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");

  // Edit state
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);
  const [editText, setEditText] = useState("");
  const [editOverrides, setEditOverrides] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [editResult, setEditResult] = useState<string | null>(null);

  // Delete confirm modal
  const [deletingPost, setDeletingPost] = useState<SocialPost | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Reschedule modal
  const [reschedulingPost, setReschedulingPost] = useState<SocialPost | null>(null);
  const [rescheduleDateTime, setRescheduleDateTime] = useState("");
  const [rescheduling, setRescheduling] = useState(false);

  // Analytics modal
  const [expandedPosts, setExpandedPosts] = useState<Set<number>>(new Set());
  const [mediaPreview, setMediaPreview] = useState<{ items: { url: string; type: string }[]; index: number } | null>(null);

  // Retry state
  const [retryingPostId, setRetryingPostId] = useState<number | null>(null);

  // Comments state
  const [commentsPostId, setCommentsPostId] = useState<number | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsPlatform, setCommentsPlatform] = useState("");

  const [analyticsPost, setAnalyticsPost] = useState<SocialPost | null>(null);
  const [analyticsData, setAnalyticsData] = useState<PostAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");

  const PAGE_SIZE = 20;
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (ssrPosts) return;
    loadData();
  }, []);

  async function loadData() {
    try {
      const postsRes = await social.listPosts({ sort: "created_at:desc", limit: PAGE_SIZE, offset: 0 });
      setPosts(postsRes.data || []);
      setOffset((postsRes.data || []).length);
      if (typeof postsRes.total === "number") setTotal(postsRes.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await social.listPosts({ sort: "created_at:desc", limit: PAGE_SIZE, offset });
      const next = res.data || [];
      setPosts((prev) => [...prev, ...next]);
      setOffset((prev) => prev + next.length);
      if (typeof res.total === "number") setTotal(res.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }

  async function syncPinterestPosts() {
    if (syncing) return;
    setSyncing(true);
    setError("");
    setSyncMessage("");
    try {
      const result = await social.syncPinterestPosts();
      await loadData();
      const imported = result.data?.imported || 0;
      const existing = result.data?.skipped_existing || 0;
      setSyncMessage(imported > 0
        ? `Imported ${imported} Pinterest post${imported === 1 ? "" : "s"}`
        : existing > 0
          ? "Pinterest posts are up to date"
          : "No Pins found on the connected board");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }

  const hasMore = total === null ? posts.length >= PAGE_SIZE && posts.length === offset : posts.length < total;

  function openEdit(post: SocialPost) {
    setEditingPost(post);
    setEditText(post.text || "");
    // Clone overrides, but clear platform text/caption fields that match the main post text
    // (they're redundant — the backend falls back to post text automatically)
    const overrides = post.platform_overrides ? JSON.parse(JSON.stringify(post.platform_overrides)) : {};
    const mainText = post.text || "";
    for (const platform of Object.keys(overrides)) {
      if (overrides[platform]?.text === mainText) delete overrides[platform].text;
    }
    setEditOverrides(overrides);
    setEditResult(null);
  }

  function setEditPlatformField(platform: string, field: string, value: any) {
    setEditOverrides(prev => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value }
    }));
  }

  function getEditPlatforms(): string[] {
    if (!editingPost) return [];
    const platforms = new Set<string>();
    const results = editingPost.platform_results || {};
    for (const r of Object.values(results)) {
      if (r.platform) platforms.add(r.platform);
    }
    // Also check account_ids for posts that never published
    if (platforms.size === 0 && editingPost.account_ids) {
      for (const aid of editingPost.account_ids) {
        const acct = accountMap[String(aid)];
        if (acct) platforms.add(acct.platform);
      }
    }
    return Array.from(platforms);
  }

  async function handleSaveEdit() {
    if (!editingPost) return;
    setSaving(true);
    setEditResult(null);
    try {
      const payload: { text: string; platform_overrides?: Record<string, any> } = { text: editText };
      // Include overrides if they changed
      const origOverrides = editingPost.platform_overrides || {};
      if (JSON.stringify(editOverrides) !== JSON.stringify(origOverrides)) {
        payload.platform_overrides = editOverrides;
      }
      const res = await social.updatePost(editingPost.id, payload);
      const pe = (res as any).data?.platform_edits;
      const parts: string[] = [];
      if (pe?.updated > 0) parts.push(`Updated on ${pe.updated} platform${pe.updated > 1 ? "s" : ""}`);
      if (pe?.skipped > 0) parts.push(`${pe.skipped} skipped (not editable)`);
      if (pe?.failed > 0) {
        const failedDetails = Object.values(pe.details as Record<string, any>)
          .filter((d: any) => d.failed)
          .map((d: any) => d.error)
          .join("; ");
        parts.push(`${pe.failed} failed: ${failedDetails}`);
      }
      setEditResult(parts.join(". ") || "Saved");
      await loadData();
    } catch (err: any) {
      setEditResult(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deletingPost) return;
    setDeleting(true);
    try {
      const res = await social.deletePost(deletingPost.id);
      const pd = (res as any).data?.platform_deletions;
      if (pd && pd.failed > 0) {
        const failedDetails = Object.values(pd.details as Record<string, any>)
          .filter((d: any) => d.failed)
          .map((d: any) => d.error)
          .join("; ");
        setError(`Post deleted but some platform deletions failed: ${failedDetails}`);
      }
      setDeletingPost(null);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  async function openAnalytics(post: SocialPost) {
    setAnalyticsPost(post);
    setAnalyticsData(null);
    setAnalyticsError("");
    setAnalyticsLoading(true);
    try {
      const res = await social.getPostAnalytics(post.id);
      setAnalyticsData(res.data);
    } catch (err: any) {
      setAnalyticsError(err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  }

  async function handleRetry(post: SocialPost) {
    setRetryingPostId(post.id);
    try {
      await social.retryPost(post.id);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRetryingPostId(null);
    }
  }

  async function toggleComments(post: SocialPost) {
    if (commentsPostId === post.id) {
      setCommentsPostId(null);
      setComments([]);
      return;
    }
    setCommentsPostId(post.id);
    setComments([]);
    setCommentsLoading(true);
    setCommentsPlatform("");

    // Find the first published platform result with a post ID
    const results = getResults(post);
    const published = results.find(r => (r.status === "published" || r.status === "success") && r.platform_post_id);
    if (!published) {
      setCommentsLoading(false);
      return;
    }

    // Find the account ID for this result
    const accountId = Object.entries(post.platform_results || {}).find(
      ([_, r]) => r.platform_post_id === published.platform_post_id
    )?.[0];

    if (!accountId) {
      setCommentsLoading(false);
      return;
    }

    setCommentsPlatform(published.platform);
    try {
      const res = await social.getInboxComments({
        social_account_id: parseInt(accountId),
        platform_post_id: published.platform_post_id
      });
      setComments(res.data?.comments || []);
    } catch (err: any) {
      console.error("Failed to load comments:", err.message);
    } finally {
      setCommentsLoading(false);
    }
  }

  async function handleCancelScheduled(post: SocialPost) {
    if (!confirm("Cancel this scheduled post? It will be moved back to draft.")) return;
    try {
      await social.cancelScheduledPost(post.id);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  }

  function openReschedule(post: SocialPost) {
    setReschedulingPost(post);
    // Pre-fill with current scheduled time in local datetime-local format
    if (post.scheduled_for) {
      const d = new Date(post.scheduled_for);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setRescheduleDateTime(local);
    } else {
      setRescheduleDateTime("");
    }
  }

  async function handleReschedule() {
    if (!reschedulingPost || !rescheduleDateTime) return;
    const scheduledFor = new Date(rescheduleDateTime);
    if (scheduledFor <= new Date()) {
      setError("Scheduled time must be in the future");
      return;
    }
    setRescheduling(true);
    try {
      await social.reschedulePost(reschedulingPost.id, {
        scheduled_for: scheduledFor.toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setReschedulingPost(null);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRescheduling(false);
    }
  }

  function getResults(post: SocialPost): PlatformResult[] {
    if (!post.platform_results) return [];
    return Object.values(post.platform_results).map((r: any) => {
      if (!r.account_name && r.accountId && accountMap[r.accountId]) {
        const acct = accountMap[r.accountId];
        return { ...r, account_name: acct.platform_display_name || acct.platform_username || r.platform };
      }
      return r;
    });
  }

  function getEditablePlatforms(post: SocialPost): string[] {
    return getResults(post)
      .filter((r) => (r.status === "published" || r.status === "success") && EDITABLE_PLATFORMS.has(r.platform))
      .map((r) => r.account_name || r.platform);
  }

  function getNonEditablePlatforms(post: SocialPost): string[] {
    return getResults(post)
      .filter((r) => (r.status === "published" || r.status === "success") && !EDITABLE_PLATFORMS.has(r.platform))
      .map((r) => r.account_name || r.platform);
  }

  function getDeleteInfo(post: SocialPost) {
    const results = getResults(post);
    const published = results.filter((r) => r.status === "published" || r.status === "success");
    const deletable = published.filter((r) => DELETABLE_PLATFORMS.has(r.platform));
    const notDeletable = published.filter((r) => !DELETABLE_PLATFORMS.has(r.platform));
    return { deletable, notDeletable };
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-stone-900 dark:text-white tracking-tight">Posts</h2>
          <p className="text-sm text-stone-500 dark:text-zinc-500 mt-1">
            {total ?? posts.length} post{(total ?? posts.length) !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={syncPinterestPosts}
            disabled={syncing}
            className="px-4 py-2.5 border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-stone-700 dark:text-zinc-300 rounded-xl text-sm font-semibold hover:border-stone-300 dark:hover:border-zinc-600 disabled:opacity-50 transition-all"
          >
            {syncing ? "Syncing…" : "Sync Pinterest"}
          </button>
          <button
            onClick={() => navigate("/compose")}
            className="px-4 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition-all"
          >
            New Post
          </button>
        </div>
      </div>

      {syncMessage && (
        <div className="p-3 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 rounded-xl mb-4 text-sm border border-emerald-200/60 dark:border-emerald-500/20">
          {syncMessage}
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl mb-4 text-sm border border-red-200/60 dark:border-red-500/20 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError("")} className="text-red-400 hover:text-red-600 dark:hover:text-red-300 text-xs font-medium ml-3">dismiss</button>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deletingPost && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !deleting && setDeletingPost(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-stone-200/60 dark:border-zinc-800/60" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-50 dark:bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-900 dark:text-white">Delete Post</h3>
                <p className="text-sm text-stone-500 dark:text-zinc-500">This action cannot be undone.</p>
              </div>
            </div>

            <div className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3 mb-4">
              <p className="text-sm text-stone-700 dark:text-zinc-300 line-clamp-2">
                {deletingPost.text || "No text content"}
              </p>
            </div>

            {(() => {
              const { deletable, notDeletable } = getDeleteInfo(deletingPost);
              return (
                <div className="space-y-2 mb-5">
                  {deletable.length > 0 && (
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-emerald-500 mt-0.5">&#10003;</span>
                      <span className="text-stone-600 dark:text-zinc-400">
                        Will be removed from: <span className="font-medium text-stone-800 dark:text-zinc-200">{deletable.map((r) => r.account_name || r.platform).join(", ")}</span>
                      </span>
                    </div>
                  )}
                  {notDeletable.length > 0 && (
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-amber-500 mt-0.5">!</span>
                      <span className="text-stone-500 dark:text-zinc-500">
                        Will remain live on: {notDeletable.map((r) => r.account_name || r.platform).join(", ")} <span className="text-stone-400 dark:text-zinc-600">(no API delete support)</span>
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingPost(null)}
                disabled={deleting}
                className="px-4 py-2.5 text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 rounded-xl transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Delete Post"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingPost && (() => {
        const editPlatforms = getEditPlatforms();
        const isFailed = editingPost.status === "failed" || editingPost.status === "partial";
        const isScheduled = editingPost.status === "scheduled";
        const isPrePublish = isFailed || isScheduled;
        const hasChanged = editText !== (editingPost.text || "") || JSON.stringify(editOverrides) !== JSON.stringify(editingPost.platform_overrides || {});
        return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !saving && setEditingPost(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-stone-200/60 dark:border-zinc-800/60 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-zinc-900 px-6 pt-6 pb-3 border-b border-stone-100 dark:border-zinc-800/60 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-stone-900 dark:text-white">Edit Post</h3>
                  {isPrePublish && (
                    <p className="text-xs text-stone-500 dark:text-zinc-500 mt-0.5">
                      {isScheduled ? "Edit your content and platform settings before publishing" : "Edit your content and platform settings before retrying"}
                    </p>
                  )}
                </div>
                <button onClick={() => setEditingPost(null)} className="text-stone-400 dark:text-zinc-600 hover:text-stone-600 dark:hover:text-zinc-400 p-1">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-5">
              {/* Text editor */}
              <div>
                <label className="text-xs font-semibold text-stone-500 dark:text-zinc-500 uppercase tracking-wide mb-2 block">Post Text</label>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={8}
                  disabled={saving}
                  placeholder="What's on your mind?"
                  className="w-full px-4 py-3.5 rounded-xl border border-stone-200 dark:border-zinc-700 bg-stone-50 dark:bg-zinc-800/50 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 resize-y text-sm leading-relaxed disabled:opacity-50 transition-colors font-[inherit]"
                />
                <div className="flex justify-between mt-1.5 text-xs text-stone-400 dark:text-zinc-600">
                  <span>{editText.length} characters</span>
                  {editPlatforms.includes("twitter") && editText.length > 280 && (
                    <span className="text-amber-500">Exceeds Twitter's 280 char limit</span>
                  )}
                </div>
              </div>

              {/* Platform sync info for published posts */}
              {!isPrePublish && (
                <div className="space-y-1.5">
                  {getEditablePlatforms(editingPost).length > 0 && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      Will be updated on: {getEditablePlatforms(editingPost).join(", ")}
                    </p>
                  )}
                  {getNonEditablePlatforms(editingPost).length > 0 && (
                    <p className="text-xs text-stone-400 dark:text-zinc-600">
                      Cannot be edited on: {getNonEditablePlatforms(editingPost).join(", ")} (no API support)
                    </p>
                  )}
                </div>
              )}

              {/* Platform-specific settings */}
              {editPlatforms.length > 0 && (
                <div>
                  <label className="text-xs font-semibold text-stone-500 dark:text-zinc-500 uppercase tracking-wide mb-2 block">Platform Settings</label>
                  <div className="space-y-3">
                    {/* YouTube */}
                    {editPlatforms.includes("youtube") && (
                      <div className="p-4 bg-stone-50 dark:bg-zinc-800/40 rounded-xl border border-stone-200/60 dark:border-zinc-700/60">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-6 h-6 bg-red-600 text-white rounded flex items-center justify-center flex-shrink-0">
                            <PlatformIcon platform="youtube" className="w-3.5 h-3.5" />
                          </span>
                          <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">YouTube</span>
                        </div>
                        <div className="space-y-2.5">
                          <input
                            type="text"
                            value={editOverrides.youtube?.title || ""}
                            onChange={(e) => setEditPlatformField("youtube", "title", e.target.value)}
                            placeholder="Video title (falls back to post text)"
                            maxLength={100}
                            disabled={saving}
                            className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 disabled:opacity-50"
                          />
                          <textarea
                            value={editOverrides.youtube?.description || ""}
                            onChange={(e) => setEditPlatformField("youtube", "description", e.target.value)}
                            placeholder="Video description (falls back to post text)"
                            rows={2}
                            disabled={saving}
                            className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 resize-none disabled:opacity-50"
                          />
                          <input
                            type="text"
                            value={(editOverrides.youtube?.tags || []).join(", ")}
                            onChange={(e) => setEditPlatformField("youtube", "tags", e.target.value.split(",").map((t: string) => t.trim()).filter(Boolean))}
                            placeholder="Tags (comma-separated)"
                            disabled={saving}
                            className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 disabled:opacity-50"
                          />
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className="text-[10px] text-stone-400 dark:text-zinc-600 mb-0.5 block uppercase tracking-wide">Privacy</label>
                              <select
                                value={editOverrides.youtube?.privacyStatus || "public"}
                                onChange={(e) => setEditPlatformField("youtube", "privacyStatus", e.target.value)}
                                disabled={saving}
                                className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white text-sm disabled:opacity-50"
                              >
                                <option value="public">Public</option>
                                <option value="unlisted">Unlisted</option>
                                <option value="private">Private</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TikTok */}
                    {editPlatforms.includes("tiktok") && (
                      <div className="p-4 bg-stone-50 dark:bg-zinc-800/40 rounded-xl border border-stone-200/60 dark:border-zinc-700/60">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-6 h-6 bg-black text-white rounded flex items-center justify-center flex-shrink-0">
                            <PlatformIcon platform="tiktok" className="w-3.5 h-3.5" />
                          </span>
                          <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">TikTok</span>
                        </div>
                        {(() => {
                          const editHasVideo = editingPost?.media_urls?.some(m => m.type === "video");
                          const editHasImages = editingPost?.media_urls?.some(m => m.type === "image") && !editHasVideo;
                          return (
                        <div className="space-y-2.5">
                          {editHasImages && (
                            <input
                              type="text"
                              value={editOverrides.tiktok?.title || ""}
                              onChange={(e) => setEditPlatformField("tiktok", "title", e.target.value)}
                              placeholder="Title (optional heading above caption)"
                              maxLength={90}
                              disabled={saving}
                              className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 disabled:opacity-50"
                            />
                          )}
                          <textarea
                            value={editOverrides.tiktok?.text || ""}
                            onChange={(e) => setEditPlatformField("tiktok", "text", e.target.value)}
                            placeholder={editHasVideo ? "Video caption (falls back to post text)" : "Photo caption (falls back to post text)"}
                            rows={2}
                            maxLength={editHasVideo ? 2200 : 4000}
                            disabled={saving}
                            className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 resize-none disabled:opacity-50"
                          />
                          <div>
                            <label className="text-[10px] text-stone-400 dark:text-zinc-600 mb-0.5 block uppercase tracking-wide">Privacy</label>
                            <select
                              value={editOverrides.tiktok?.privacyLevel || "PUBLIC_TO_EVERYONE"}
                              onChange={(e) => setEditPlatformField("tiktok", "privacyLevel", e.target.value)}
                              disabled={saving}
                              className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white text-sm disabled:opacity-50"
                            >
                              <option value="PUBLIC_TO_EVERYONE">Public</option>
                              <option value="MUTUAL_FOLLOW_FRIENDS">Friends only</option>
                              <option value="FOLLOWER_OF_CREATOR">Followers only</option>
                              <option value="SELF_ONLY">Only me</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-4">
                            {editHasImages && (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editOverrides.tiktok?.autoAddMusic || false}
                                  onChange={(e) => setEditPlatformField("tiktok", "autoAddMusic", e.target.checked)}
                                  disabled={saving}
                                  className="w-4 h-4 rounded border-stone-300 dark:border-zinc-600 text-orange-500 focus:ring-orange-500/40"
                                />
                                <span className="text-sm text-stone-700 dark:text-zinc-300">Auto-add music</span>
                              </label>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editOverrides.tiktok?.disableComment || false}
                                onChange={(e) => setEditPlatformField("tiktok", "disableComment", e.target.checked)}
                                disabled={saving}
                                className="w-4 h-4 rounded border-stone-300 dark:border-zinc-600 text-orange-500 focus:ring-orange-500/40"
                              />
                              <span className="text-sm text-stone-700 dark:text-zinc-300">Disable comments</span>
                            </label>
                          </div>
                        </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Reddit */}
                    {editPlatforms.includes("reddit") && (
                      <div className="p-4 bg-stone-50 dark:bg-zinc-800/40 rounded-xl border border-stone-200/60 dark:border-zinc-700/60">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-6 h-6 bg-orange-600 text-white rounded flex items-center justify-center flex-shrink-0">
                            <PlatformIcon platform="reddit" className="w-3.5 h-3.5" />
                          </span>
                          <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">Reddit</span>
                        </div>
                        <input
                          type="text"
                          value={editOverrides.reddit?.title || ""}
                          onChange={(e) => setEditPlatformField("reddit", "title", e.target.value)}
                          placeholder="Post title (falls back to post text)"
                          maxLength={300}
                          disabled={saving}
                          className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 disabled:opacity-50"
                        />
                      </div>
                    )}

                    {/* Pinterest */}
                    {editPlatforms.includes("pinterest") && (
                      <div className="p-4 bg-stone-50 dark:bg-zinc-800/40 rounded-xl border border-stone-200/60 dark:border-zinc-700/60">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="w-6 h-6 bg-red-700 text-white rounded flex items-center justify-center flex-shrink-0">
                            <PlatformIcon platform="pinterest" className="w-3.5 h-3.5" />
                          </span>
                          <span className="text-sm font-semibold text-stone-700 dark:text-zinc-300">Pinterest</span>
                        </div>
                        <input
                          type="text"
                          value={editOverrides.pinterest?.title || ""}
                          onChange={(e) => setEditPlatformField("pinterest", "title", e.target.value)}
                          placeholder="Pin title (falls back to post text)"
                          maxLength={100}
                          disabled={saving}
                          className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-600 text-sm focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 dark:focus:border-orange-500 disabled:opacity-50"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Result message */}
              {editResult && (
                <div className={`p-3 rounded-xl text-xs ${
                  editResult.includes("Error") || editResult.includes("failed")
                    ? "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                    : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                }`}>
                  {editResult}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white dark:bg-zinc-900 px-6 py-4 border-t border-stone-200/60 dark:border-zinc-800/60 flex justify-end gap-3">
              <button
                onClick={() => setEditingPost(null)}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 rounded-xl transition-colors disabled:opacity-50"
              >
                {editResult ? "Close" : "Cancel"}
              </button>
              {!editResult && (
                <button
                  onClick={handleSaveEdit}
                  disabled={saving || !hasChanged}
                  className="px-5 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition-all"
                >
                  {saving ? "Saving..." : isPrePublish ? "Save" : "Save & Sync"}
                </button>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Analytics modal */}
      {analyticsPost && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setAnalyticsPost(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-lg p-6 border border-stone-200/60 dark:border-zinc-800/60 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-50 dark:bg-orange-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-stone-900 dark:text-white">Post Analytics</h3>
              </div>
              <button onClick={() => setAnalyticsPost(null)} className="text-stone-400 dark:text-zinc-600 hover:text-stone-600 dark:hover:text-zinc-400 p-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {analyticsLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {analyticsError && (
              <div className="p-3 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 rounded-xl text-sm">
                {analyticsError}
              </div>
            )}

            {analyticsData && !analyticsLoading && (
              <div className="space-y-4">
                {/* Totals */}
                {Object.keys(analyticsData.totals).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-2 tracking-widest">Totals</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(analyticsData.totals).map(([key, value]) => (
                        <div key={key} className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold text-stone-900 dark:text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
                          <p className="text-[10px] uppercase text-stone-400 dark:text-zinc-600 font-medium tracking-wide">{key.replace(/_/g, " ")}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(analyticsData.totals).length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-stone-500 dark:text-zinc-500 text-sm">No metrics available yet</p>
                    <p className="text-stone-400 dark:text-zinc-600 text-xs mt-1">Analytics may take a few minutes to become available after posting</p>
                  </div>
                )}

                {/* Per-platform breakdown */}
                {Object.keys(analyticsData.analytics).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-2 tracking-widest">By Platform</p>
                    <div className="space-y-2">
                      {Object.entries(analyticsData.analytics).map(([platform, entry]) => {
                        const info = getPlatformInfo(platform);
                        const hasMetrics = entry.metrics && Object.keys(entry.metrics).length > 0;
                        return (
                          <div key={platform} className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-5 h-5 ${info.color} text-white rounded flex items-center justify-center flex-shrink-0`}>
                                <PlatformIcon platform={platform} className="w-3 h-3" />
                              </span>
                              <span className="text-sm font-medium text-stone-800 dark:text-zinc-200">
                                {entry.account_name || info.name}
                              </span>
                              {entry.platform_post_url && (
                                <a href={entry.platform_post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium ml-auto">
                                  view
                                </a>
                              )}
                            </div>
                            {hasMetrics ? (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {Object.entries(entry.metrics!).filter(([_, v]) => v != null).map(([key, value]) => (
                                  <span key={key} className="text-xs text-stone-600 dark:text-zinc-400">
                                    <span className="font-semibold text-stone-800 dark:text-zinc-200">{typeof value === 'number' ? value.toLocaleString() : value}</span>
                                    {" "}{key.replace(/_/g, " ")}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-stone-400 dark:text-zinc-600">{entry.reason || "No metrics available"}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reschedule modal */}
      {reschedulingPost && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !rescheduling && setReschedulingPost(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-stone-200/60 dark:border-zinc-800/60" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-yellow-50 dark:bg-yellow-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-900 dark:text-white">Reschedule Post</h3>
                <p className="text-xs text-stone-500 dark:text-zinc-500">Pick a new date and time</p>
              </div>
            </div>

            <div className="bg-stone-50 dark:bg-zinc-800/50 rounded-xl p-3 mb-4">
              <p className="text-sm text-stone-700 dark:text-zinc-300 line-clamp-2">
                {reschedulingPost.text || "No text content"}
              </p>
            </div>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-stone-500 dark:text-zinc-400 mb-2 uppercase tracking-wide">New date & time</label>
              <input
                type="datetime-local"
                value={rescheduleDateTime}
                onChange={(e) => setRescheduleDateTime(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-4 py-3 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-stone-900 dark:text-white text-sm focus:ring-2 focus:ring-yellow-500/40 focus:border-yellow-500 dark:focus:border-yellow-500 transition-colors [color-scheme:light] dark:[color-scheme:dark]"
              />
              <p className="text-xs text-stone-400 dark:text-zinc-500 mt-1.5">
                Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReschedulingPost(null)}
                disabled={rescheduling}
                className="px-4 py-2.5 text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800 rounded-xl transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReschedule}
                disabled={rescheduling || !rescheduleDateTime}
                className="px-4 py-2.5 bg-yellow-500 text-white rounded-xl text-sm font-semibold hover:bg-yellow-600 disabled:opacity-50 transition-colors"
              >
                {rescheduling ? "Updating..." : "Reschedule"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media preview modal / gallery */}
      {mediaPreview && (() => {
        const current = mediaPreview.items[mediaPreview.index];
        const total = mediaPreview.items.length;
        const hasPrev = mediaPreview.index > 0;
        const hasNext = mediaPreview.index < total - 1;
        return (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setMediaPreview(null)}>
            <button onClick={() => setMediaPreview(null)} className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            {total > 1 && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm font-medium px-3 py-1 rounded-full z-10">
                {mediaPreview.index + 1} / {total}
              </div>
            )}
            {hasPrev && (
              <button
                onClick={(e) => { e.stopPropagation(); setMediaPreview({ ...mediaPreview, index: mediaPreview.index - 1 }); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center z-10"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
            )}
            {hasNext && (
              <button
                onClick={(e) => { e.stopPropagation(); setMediaPreview({ ...mediaPreview, index: mediaPreview.index + 1 }); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center z-10"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            )}
            <div className="max-w-4xl max-h-[85vh] w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {current.type === "image" ? (
                <img src={current.url} alt="" className="max-w-full max-h-[85vh] object-contain rounded-xl" />
              ) : (
                <video src={current.url} controls autoPlay className="max-w-full max-h-[85vh] rounded-xl" />
              )}
            </div>
            {total > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {mediaPreview.items.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setMediaPreview({ ...mediaPreview, index: i }); }}
                    className={`w-2 h-2 rounded-full transition-colors ${i === mediaPreview.index ? "bg-white" : "bg-white/40 hover:bg-white/70"}`}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {loading ? (
        <PostListSkeleton />
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-stone-100 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-stone-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
          <p className="text-stone-500 dark:text-zinc-500 mb-4">No posts yet</p>
          <button
            onClick={() => navigate("/compose")}
            className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 text-sm font-semibold"
          >
            Create your first post
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const results = getResults(post);
            const isPublished = post.status === "published" || post.status === "partial";
            const hasMedia = post.media_urls && post.media_urls.length > 0;
            const firstImage = post.media_urls?.find(m => m.type === "image");
            const firstVideo = post.media_urls?.find(m => m.type === "video");

            return (
              <div
                key={post.id}
                className={`bg-white dark:bg-zinc-900 rounded-2xl border overflow-hidden transition-colors ${
                  post.status === "scheduled"
                    ? "border-yellow-300 dark:border-yellow-500/30 hover:border-yellow-400 dark:hover:border-yellow-500/50"
                    : "border-stone-200/60 dark:border-zinc-800/60 hover:border-stone-300 dark:hover:border-zinc-700"
                }`}
              >
                <div className={`flex ${hasMedia ? "flex-row" : ""}`}>
                  {/* Media thumbnail on left side */}
                  {firstImage && (
                    <div
                      className="w-48 flex-shrink-0 cursor-pointer relative group self-stretch"
                      onClick={() => setMediaPreview({ items: post.media_urls!.map(m => ({ url: m.url, type: m.type })), index: 0 })}
                    >
                      <img src={firstImage.url} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <svg className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                      {post.media_urls!.length > 1 && (
                        <span className="absolute top-2 right-2 bg-black/70 text-white text-[11px] font-semibold px-2 py-0.5 rounded-lg flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          {post.media_urls!.length}
                        </span>
                      )}
                    </div>
                  )}
                  {!firstImage && firstVideo && (
                    <div
                      className="w-48 flex-shrink-0 bg-black cursor-pointer relative group self-stretch"
                      onClick={() => setMediaPreview({ items: post.media_urls!.map(m => ({ url: m.url, type: m.type })), index: 0 })}
                    >
                      {/*
                        preload="metadata" + a tiny t=0.001 seek makes the
                        browser fetch just the MP4 moov atom + first GOP
                        (~100-300 KB) and render the first frame as a
                        thumbnail. The rest of the video isn't downloaded
                        until the user clicks into the modal — net cost is
                        ~1% of streaming the whole file.
                      */}
                      <video
                        ref={(el) => {
                          if (!el) return;
                          const onMeta = () => {
                            try { el.currentTime = 0.001; } catch {}
                          };
                          el.addEventListener("loadedmetadata", onMeta, { once: true });
                        }}
                        src={firstVideo.url}
                        muted
                        preload="metadata"
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <svg className="w-10 h-10 text-white opacity-70 group-hover:opacity-100 transition-opacity drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      {post.media_urls!.length > 1 && (
                        <span className="absolute top-2 right-2 bg-black/70 text-white text-[11px] font-semibold px-2 py-0.5 rounded-lg flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          {post.media_urls!.length}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 p-4 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        {post.platform_overrides?.youtube?.title ? (
                          <>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="w-4 h-4 bg-red-600 text-white rounded flex items-center justify-center flex-shrink-0">
                                <PlatformIcon platform="youtube" className="w-2.5 h-2.5" />
                              </span>
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 dark:text-zinc-600">Title</span>
                            </div>
                            <p className="text-stone-900 dark:text-zinc-100 text-sm font-semibold mb-1.5 truncate">
                              {post.platform_overrides.youtube.title}
                            </p>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 dark:text-zinc-600">Description</span>
                            <p className={`text-stone-700 dark:text-zinc-300 text-sm whitespace-pre-wrap break-words leading-relaxed mt-0.5 ${expandedPosts.has(post.id) ? "" : "line-clamp-3"}`}>
                              {post.platform_overrides.youtube.description || post.text || <span className="text-stone-400 dark:text-zinc-600 italic">No description</span>}
                            </p>
                          </>
                        ) : post.platform_overrides?.tiktok?.title ? (
                          <>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="w-4 h-4 bg-black text-white rounded flex items-center justify-center flex-shrink-0">
                                <PlatformIcon platform="tiktok" className="w-2.5 h-2.5" />
                              </span>
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 dark:text-zinc-600">Title</span>
                            </div>
                            <p className="text-stone-900 dark:text-zinc-100 text-sm font-semibold mb-1.5 truncate">
                              {post.platform_overrides.tiktok.title}
                            </p>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 dark:text-zinc-600">Caption</span>
                            <p className={`text-stone-700 dark:text-zinc-300 text-sm whitespace-pre-wrap break-words leading-relaxed mt-0.5 ${expandedPosts.has(post.id) ? "" : "line-clamp-3"}`}>
                              {post.platform_overrides.tiktok.text || post.text || <span className="text-stone-400 dark:text-zinc-600 italic">No caption</span>}
                            </p>
                          </>
                        ) : (
                          <p className={`text-stone-900 dark:text-zinc-100 text-sm whitespace-pre-wrap break-words leading-relaxed ${expandedPosts.has(post.id) ? "" : "line-clamp-5"}`}>
                            {post.text || <span className="text-stone-400 dark:text-zinc-600 italic">No text</span>}
                          </p>
                        )}
                        {((post.platform_overrides?.youtube?.description || post.platform_overrides?.tiktok?.text || post.text || "").length > 250) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedPosts(prev => {
                                const next = new Set(prev);
                                if (next.has(post.id)) next.delete(post.id);
                                else next.add(post.id);
                                return next;
                              });
                            }}
                            className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 text-xs font-medium mt-1"
                          >
                            {expandedPosts.has(post.id) ? "Show less" : "Show more"}
                          </button>
                        )}
                        {post.link_url && (
                          <a
                            href={post.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 dark:text-orange-400 text-xs hover:text-orange-700 dark:hover:text-orange-300 mt-1 block truncate font-medium"
                          >
                            {post.link_url}
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {Object.values(post.platform_overrides || {}).some(
                          (o: any) => o && o.post_format === "story",
                        ) && (
                          <span className="px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-500/10 dark:text-fuchsia-400">
                            Story
                          </span>
                        )}
                        <StatusBadge status={post.status} scheduledFor={post.scheduled_for} />
                        {isPublished && (
                          <button
                            onClick={() => openAnalytics(post)}
                            className="text-stone-400 dark:text-zinc-600 hover:text-orange-500 dark:hover:text-orange-400 transition-colors p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-zinc-800"
                            title="View analytics"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </button>
                        )}
                        {(isPublished || post.status === "failed" || post.status === "partial" || post.status === "scheduled") && (
                          <button
                            onClick={() => openEdit(post)}
                            className="text-stone-400 dark:text-zinc-600 hover:text-orange-500 dark:hover:text-orange-400 transition-colors p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-zinc-800"
                            title={post.status === "failed" ? "Edit before retrying" : post.status === "scheduled" ? "Edit before publishing" : "Edit post"}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        {(post.status === "failed" || post.status === "partial") && (
                          <button
                            onClick={() => handleRetry(post)}
                            disabled={retryingPostId === post.id}
                            className="text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 transition-colors p-1.5 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-500/10 disabled:opacity-50"
                            title="Retry failed platforms"
                          >
                            {retryingPostId === post.id ? (
                              <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            )}
                          </button>
                        )}
                        {post.status === "scheduled" && (
                          <button
                            onClick={() => openReschedule(post)}
                            className="text-yellow-500 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-300 transition-colors p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-zinc-800"
                            title="Change scheduled time"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                        {post.status === "scheduled" && (
                          <button
                            onClick={() => handleCancelScheduled(post)}
                            className="text-stone-400 dark:text-zinc-600 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-zinc-800"
                            title="Cancel scheduled post"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => setDeletingPost(post)}
                          className="text-stone-400 dark:text-zinc-600 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-zinc-800"
                          title="Delete post"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Scheduled info */}
                    {post.status === "scheduled" && post.scheduled_for && (
                      <div className="mt-3 pt-3 border-t border-yellow-100 dark:border-yellow-500/10">
                        <div className="flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-400">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">
                            Scheduled for {new Date(post.scheduled_for).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} at {new Date(post.scheduled_for).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                          </span>
                          {post.timezone && (
                            <span className="text-xs text-yellow-500 dark:text-yellow-500/70">({post.timezone})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-2">
                          <span className="text-[10px] uppercase font-semibold text-yellow-600/70 dark:text-yellow-500/50 tracking-widest mr-1">Will post to</span>
                          {(post.account_ids || []).map((aid) => {
                            const acct = accountMap[String(aid)];
                            if (!acct) return null;
                            const info = getPlatformInfo(acct.platform);
                            return (
                              <div
                                key={aid}
                                className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-yellow-200/60 dark:border-yellow-500/20 bg-yellow-50/50 dark:bg-yellow-500/5 text-xs"
                              >
                                <span className={`w-4 h-4 ${info.color} text-white rounded flex items-center justify-center flex-shrink-0`}>
                                  <PlatformIcon platform={acct.platform} className="w-2.5 h-2.5" />
                                </span>
                                <span className="text-yellow-700 dark:text-yellow-300 font-medium truncate max-w-[120px]">
                                  {acct.platform_display_name || acct.platform_username || info.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Platform results */}
                    {results.length > 0 && post.status !== "scheduled" && (
                      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-zinc-800/60">
                        <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 mb-1.5 tracking-widest">
                          {post.status === "draft" ? "Draft destination" : "Posted to"}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {results.map((r, i) => (
                            <PlatformResultItem key={i} result={r} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Meta info + comments toggle */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-stone-400 dark:text-zinc-600">
                      {post.published_at && (
                        <span>{new Date(post.published_at).toLocaleString()}</span>
                      )}
                      {!post.published_at && post.created_at && (
                        <span>{new Date(post.created_at).toLocaleString()}</span>
                      )}
                      {post.status !== "scheduled" && post.status !== "draft" && post.total_accounts > 0 && (
                        <span>{post.successful_count}/{post.total_accounts} succeeded</span>
                      )}
                      {isPublished && results.some(r => r.platform_post_id) && (
                        <button
                          onClick={() => toggleComments(post)}
                          className={`flex items-center gap-1 font-medium transition-colors ${
                            commentsPostId === post.id
                              ? "text-orange-500 dark:text-orange-400"
                              : "hover:text-stone-600 dark:hover:text-zinc-400"
                          }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          {commentsPostId === post.id ? "Hide comments" : "Comments"}
                        </button>
                      )}
                    </div>

                    {/* Expandable comments section */}
                    {commentsPostId === post.id && (
                      <div className="mt-3 pt-3 border-t border-stone-100 dark:border-zinc-800/60">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 tracking-widest">
                            Comments
                          </p>
                          {commentsPlatform && (
                            <span className="text-[10px] text-stone-400 dark:text-zinc-600">
                              via {getPlatformInfo(commentsPlatform).name}
                            </span>
                          )}
                        </div>

                        {commentsLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : comments.length === 0 ? (
                          <p className="text-xs text-stone-400 dark:text-zinc-600 py-2">No comments yet</p>
                        ) : (
                          <div className="space-y-2.5 max-h-64 overflow-y-auto">
                            {comments.map((c: any) => (
                              <div key={c.id} className="flex gap-2">
                                <div className="w-6 h-6 rounded-full bg-stone-200 dark:bg-zinc-700 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-stone-500 dark:text-zinc-400">
                                  {(c.from_name || "?")[0].toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline gap-1.5">
                                    <span className="text-xs font-semibold text-stone-700 dark:text-zinc-300 truncate">{c.from_name || "User"}</span>
                                    {c.created_time && (
                                      <span className="text-[10px] text-stone-400 dark:text-zinc-600 flex-shrink-0">
                                        {new Date(c.created_time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-stone-600 dark:text-zinc-400 mt-0.5 break-words">{c.message}</p>
                                  <div className="flex items-center gap-3 mt-1 text-[10px] text-stone-400 dark:text-zinc-600">
                                    {c.like_count > 0 && <span>{c.like_count} likes</span>}
                                    {c.reply_count > 0 && <span>{c.reply_count} replies</span>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {hasMore && (
            <div className="flex justify-center py-6">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-5 py-2.5 rounded-xl border border-stone-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/50 text-sm font-medium text-stone-600 dark:text-zinc-300 hover:bg-stone-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loadingMore ? "Loading…" : `Load more${total ? ` · ${posts.length}/${total}` : ""}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
